import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, catchError, throwError, EMPTY } from 'rxjs';
import { Router } from '@angular/router';
import { LocalStorageService } from '../services/localStorage/local-storage.service';
import { SessionManagerService } from '../services/session-manager/session-manager.service';
import { UserService } from '../services/user/user.service';
import { DynamicRouteService } from '../services/dynamic-route/dynamic-route.service';
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private sessionManager: SessionManagerService,
    private userService: UserService,
    private dynamicRouteService: DynamicRouteService
  ) { }

  /**
   * Genera una clave de idempotencia única usando UUID v4
   */
  private generateIdempotencyKey(): string {
    // Usando crypto.randomUUID() si está disponible (navegadores modernos)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Fallback: generación manual de UUID v4
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const accessToken = this.localStorageService.getItem('access_token');
    const headers: any = {};

    if (accessToken && !request.url.includes('/public/')) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Agregar Idempotency-Key para operaciones que modifican datos
    if (['POST'].includes(request.method)) {
      headers['Idempotency-Key'] = this.generateIdempotencyKey();
    }

    if (Object.keys(headers).length > 0) {
      // Clonar la solicitud y añadir los headers
      request = request.clone({
        setHeaders: headers,
      });
    }
    return next.handle(request).pipe(
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            case 401:
              return this.handleUnAuthorizedError(request, next);
            case 404:
              return this.handle404Error(request, next, err);
            case 422:
              return this.handle422Error(request, next, err);
            case 429:
              return this.handle429Error(request, next, err);
            case 400:
              return this.handle400Error(request, next, err);
            case 409:
              return this.handle409Error(request, next, err);
            case 500:
              return this.handle500Error(request, next, err);
            default:
              return throwError(() => err);
              break;
          }
        }
        // Si no es un HttpErrorResponse, simplemente re-lanzar el error
        return throwError(() => err);
      })
    );
  }

  handle400Error(request: HttpRequest<any>, next: HttpHandler, error: any) {
    const customError = {
      code: 400,
      message: 'Recurso no encontrado',
      originalError: error,
      error: error.error?.errors,
    };
    return throwError(() => customError);
  }

  handle500Error(request: HttpRequest<any>, next: HttpHandler, error: any) {
    const customError = {
      code: 500,
      message: 'Recurso no encontrado',
      originalError: error,
      error: error.error?.errors,
    };
    return throwError(() => customError);
  }
  handle409Error(request: HttpRequest<any>, next: HttpHandler, error: any) {
    const customError = {
      code: 409,
      message: 'Recurso no encontrado',
      originalError: error,
      error: error.error?.errors,
    };
    return throwError(() => customError);
  }

  handle422Error(request: HttpRequest<any>, next: HttpHandler, error: any) {
    const customError = {
      code: 422,
      message: 'Recurso no encontrado',
      originalError: error,
      error: error.error?.errors,
    };
    return throwError(() => customError);
  }
  handle429Error(request: HttpRequest<any>, next: HttpHandler, error: any) {
    const customError = {
      code: 429,
      message: 'Too many requests',
      originalError: error,
      error: error.error?.errors || null,
    };
    return throwError(() => customError);
  }

  handle404Error(request: HttpRequest<any>, next: HttpHandler, error: any) {
    const customError = {
      code: 404,
      message: 'Recurso no encontrado',
      originalError: error,
      error: error.error?.errors,
    };
    return throwError(() => customError);
  }

  handleUnAuthorizedError(request: HttpRequest<any>, next: HttpHandler) {
    // Si el 401 viene de /auth (login), no recargar — dejar que el componente maneje el error
    if (request.url.endsWith('/auth') || request.url.includes('/auth/mfa/')) {
      const customError = {
        code: 401,
        message: 'Unauthorized',
        originalError: null,
        error: null,
      };
      return throwError(() => customError);
    }

    // Multi-account: remove only the active session, preserve others
    this.sessionManager.removeActiveSessionSilent();
    this.userService.clearUser();

    const remaining = this.sessionManager.getSessions();
    if (remaining.length > 0) {
      this.sessionManager.emitSessionExpired();
      this.router.navigate(['/login'], { queryParams: { reason: 'session_expired' } });
    } else {
      this.localStorageService.reset();
      this.dynamicRouteService.clearDynamicRoutes();
      this.router.navigate(['/login']);
    }

    return EMPTY;
  }
}
